import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Stack,
  Pagination,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Edit as EditIcon,
  Science as ScienceIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  LocalHospital as HospitalIcon,
} from "@mui/icons-material";
import { getUrgencyColor, getStatusColor } from "../../specialist-referrals/constants/referralOptions";
import { formatDate } from "../../../utils/formatters/date";

const PAGE_SIZE = 15;

export default function ReferralsLabTable({
  data = [],
  loading = false,
  onCreateLab,
  onEditLab,
  onViewReferral,
}) {
  const [page, setPage] = useState(1);

  const totalPages = Math.ceil(data.length / PAGE_SIZE);
  const paginatedData = data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Member
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Specialist
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Urgency
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Center
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Date
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Lab
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3 }}>
                    <CircularProgress size={20} />
                    <Typography>Loading referrals...</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                    No referrals found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((referral) => (
                <TableRow
                  key={referral.specialistReferralId}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => onViewReferral?.(referral)}
                >
                  {/* Member */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {referral.patientName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {referral.cipId}
                    </Typography>
                  </TableCell>

                  {/* Specialist */}
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        {referral.specialistName || "—"}
                      </Typography>
                      {referral.specialistTypeName && (
                        <Chip
                          label={referral.specialistTypeName}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.65rem" }}
                        />
                      )}
                    </Stack>
                  </TableCell>

                  {/* Urgency */}
                  <TableCell>
                    <Chip
                      label={referral.urgencyName || "—"}
                      size="small"
                      color={getUrgencyColor(referral.urgencyName)}
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Chip
                      label={referral.referralStatusName || "—"}
                      size="small"
                      color={getStatusColor(referral.referralStatusName)}
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  </TableCell>

                  {/* Center */}
                  <TableCell>
                    <Chip
                      icon={<HospitalIcon sx={{ fontSize: 14 }} />}
                      label={referral.medicalCenter || "—"}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  </TableCell>

                  {/* Date */}
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(referral.availityReferral)}
                    </Typography>
                  </TableCell>

                  {/* Lab Indicator */}
                  <TableCell align="center">
                    {referral.hasLabPhlebotomy ? (
                      <Tooltip title="Lab Phlebotomy exists">
                        <CheckIcon color="success" fontSize="small" />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      {referral.hasLabPhlebotomy ? (
                        <Tooltip title="Edit Lab Phlebotomy">
                          <IconButton
                            color="success"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditLab?.(referral);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Create Lab Phlebotomy">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCreateLab?.(referral);
                            }}
                          >
                            <ScienceIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View Referral">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewReferral?.(referral);
                          }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {(page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, data.length)} of {data.length}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}
